import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useRegisterPortalRefresh } from '../../../contexts/PortalRefreshContext';
import AdminPageShell from '../AdminPageShell';
import AdminPagination from '../AdminPagination';
import AdminToast from '../AdminToast';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import { useAdminTablePagination } from '../../../hooks/useAdminTablePagination';
import DocumentPreviewModal from '../../common/DocumentPreviewModal';
import LearnerIdentityReviewModal from './LearnerIdentityReviewModal';
import {
  fetchAdminDocuments,
  validateAdminDocument,
} from '../../../services/academiaAdminService';
import { resolveIdentityCategorisation } from '../../../utils/categorieParticipant';
import {
  APPRENANT_DOCUMENT_LABELS,
  getApprenantDocumentLabel,
  isIdentityDocumentType,
} from '../../../constants/academiaDocumentTypes';

const TYPE_LABELS: Record<string, string> = APPRENANT_DOCUMENT_LABELS;

const STATUT_LABELS: Record<string, string> = {
  EN_ATTENTE: 'En attente',
  VALIDE: 'Validé',
  REJETE: 'Rejeté',
};

type DocRow = {
  id: string;
  typeDocument: string;
  urlFichier: string;
  statutValidation: string;
  commentaireAdmin?: string | null;
  dateCreation?: string;
  profil?: {
    dateNaissance?: string | null;
    age?: number | null;
    categorie?: string | null;
    categorisationRaison?: string | null;
    profession?: string | null;
    pieceIdentite?: { id: string; statutValidation: string; typeDocument?: string } | null;
    utilisateur?: {
      email?: string;
      profil?: { prenom?: string; nom?: string };
      profilApprenant?: {
        matricule?: string;
        categorie?: string;
        profession?: string | null;
        age?: number | null;
        categorisationRaison?: string | null;
      };
    };
  };
};

const actionBtnOutline =
  'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-bold transition';

function personName(row: DocRow) {
  const u = row.profil?.utilisateur;
  const p = u?.profil;
  return [p?.prenom, p?.nom].filter(Boolean).join(' ') || u?.email || '—';
}

function identityReviewFromRow(row: DocRow) {
  const pa = row.profil?.utilisateur?.profilApprenant;
  const piece = row.profil?.pieceIdentite;
  const dateNaissance = row.profil?.dateNaissance ?? null;
  const profession = row.profil?.profession ?? pa?.profession ?? null;
  const { age, categorie, categorisationRaison } = resolveIdentityCategorisation({
    dateNaissance,
    age: row.profil?.age ?? pa?.age,
    categorie: row.profil?.categorie ?? pa?.categorie,
    categorisationRaison: row.profil?.categorisationRaison ?? pa?.categorisationRaison,
    profession,
  });

  const pieceIdentiteId =
    piece?.id ?? (isIdentityDocumentType(row.typeDocument) ? row.id : null);
  const pieceIdentiteStatut =
    piece?.statutValidation ??
    (isIdentityDocumentType(row.typeDocument) ? row.statutValidation : null);
  const pieceIdentiteType =
    piece?.typeDocument ??
    (isIdentityDocumentType(row.typeDocument) ? row.typeDocument : null);

  return {
    learnerName: personName(row),
    matricule: pa?.matricule,
    email: row.profil?.utilisateur?.email,
    dateNaissance,
    age,
    categorie,
    categorisationRaison,
    profession,
    pieceIdentiteId,
    pieceIdentiteStatut,
    pieceIdentiteType,
    pieceIdentiteLabel: pieceIdentiteType ? getApprenantDocumentLabel(pieceIdentiteType) : null,
    pieceIdentiteApiPath: pieceIdentiteId
      ? `/admin/documents/${encodeURIComponent(pieceIdentiteId)}/fichier`
      : null,
  };
}

export default function AcademiaDocumentsPanel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const handledDocumentRef = useRef<string | null>(null);
  const [filter, setFilter] = useState<'EN_ATTENTE' | 'ALL'>('EN_ATTENTE');
  const [items, setItems] = useState<DocRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectMotif, setRejectMotif] = useState('');
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<{
    title: string;
    subtitle: string;
    apiPath: string;
  } | null>(null);
  const [identityReview, setIdentityReview] = useState<DocRow | null>(null);
  const [toast, setToast] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToast({ message, variant });
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  const runAction = useCallback(
    async (action: () => Promise<void>, successMessage: string, errorFallback = 'Action impossible.') => {
      try {
        await action();
        showToast(successMessage, 'success');
      } catch (e) {
        showToast(e instanceof Error ? e.message : errorFallback, 'error');
      }
    },
    [showToast],
  );

  const { paginatedItems, currentPage, totalPages, onPageChange, summary } = useAdminTablePagination(items);

  const load = useCallback(async () => {
    try {
      setError(false);
      setLoading(true);
      const data = await fetchAdminDocuments(filter === 'EN_ATTENTE' ? 'EN_ATTENTE' : undefined);
      setItems(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useRegisterPortalRefresh(() => void load());

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const documentId = searchParams.get('document')?.trim() ?? '';
    if (!documentId || loading) return;
    if (handledDocumentRef.current === documentId) return;

    const row = items.find((entry) => entry.id === documentId);
    if (!row) {
      if (filter === 'EN_ATTENTE') {
        setFilter('ALL');
      }
      return;
    }

    handledDocumentRef.current = documentId;
    setSearchParams({}, { replace: true });

    if (row.statutValidation === 'EN_ATTENTE' && isIdentityDocumentType(row.typeDocument)) {
      setIdentityReview(row);
      return;
    }

    setPreview({
      title: TYPE_LABELS[row.typeDocument] ?? row.typeDocument,
      subtitle: personName(row),
      apiPath: `/admin/documents/${encodeURIComponent(row.id)}/fichier`,
    });
  }, [searchParams, setSearchParams, items, loading, filter]);

  const handleValidate = async (id: string) => {
    setSaving(true);
    try {
      await runAction(
        async () => {
          await validateAdminDocument(id, { statut: 'VALIDE' });
          await load();
        },
        'Document validé avec succès.',
        'Impossible de valider ce document.',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectId) return;
    if (!rejectMotif.trim()) {
      showToast('Indiquez un motif de rejet.', 'error');
      return;
    }
    setSaving(true);
    try {
      await runAction(
        async () => {
          await validateAdminDocument(rejectId, { statut: 'REJETE', commentaireAdmin: rejectMotif.trim() });
          setRejectId(null);
          setRejectMotif('');
          await load();
        },
        'Document rejeté — le motif a été enregistré.',
        'Impossible de rejeter ce document.',
      );
    } finally {
      setSaving(false);
    }
  };

  const pendingCount = items.filter((d) => d.statutValidation === 'EN_ATTENTE').length;

  return (
    <AdminPageShell
      title="Validation des dossiers"
      description="Justificatif d'identité (CNI, passeport, permis, carte d'électeur) et photo. Validez ou rejetez avec motif."
      sideCard={{
        label: filter === 'EN_ATTENTE' ? 'En attente' : 'Documents listés',
        value: loading ? '…' : String(filter === 'EN_ATTENTE' ? pendingCount : items.length),
        sideLinks: [
          { label: 'Apprenants', to: '/admin/apprenants', icon: 'group' },
          { label: 'Inscriptions', to: '/admin/inscriptions', icon: 'assignment' },
        ],
      }}
      onRetry={() => void load()}
      isLoading={loading}
      isError={error}
      loadingMessage="Chargement des documents…"
      errorMessage="Impossible de charger les documents."
    >
      <AdminToast message={toast?.message ?? null} variant={toast?.variant} onClose={dismissToast} />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2" role="tablist" aria-label="Filtrer les documents">
          <button
            type="button"
            role="tab"
            aria-selected={filter === 'EN_ATTENTE'}
            onClick={() => setFilter('EN_ATTENTE')}
            className={[
              'rounded-full px-3 py-1.5 text-sm font-semibold transition-colors',
              filter === 'EN_ATTENTE'
                ? 'bg-primary text-white shadow-sm'
                : 'border border-outline-variant/25 bg-white text-slate-700 hover:bg-surface-container-low',
            ].join(' ')}
          >
            En attente
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={filter === 'ALL'}
            onClick={() => setFilter('ALL')}
            className={[
              'rounded-full px-3 py-1.5 text-sm font-semibold transition-colors',
              filter === 'ALL'
                ? 'bg-primary text-white shadow-sm'
                : 'border border-outline-variant/25 bg-white text-slate-700 hover:bg-surface-container-low',
            ].join(' ')}
          >
            Tous
          </button>
        </div>
        {!loading && items.length > 0 ? (
          <p className="text-sm text-on-surface-variant">{summary}</p>
        ) : null}
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-on-surface-variant">Aucun document à afficher.</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-outline-variant/20 bg-surface-container-lowest shadow-sm">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-outline-variant/15 bg-surface-container-low text-xs uppercase tracking-wide text-on-surface-variant">
              <tr>
                <th className="px-4 py-3">Apprenant</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedItems.map((row) => (
                <tr key={row.id} className="border-b border-outline-variant/10 last:border-0">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{personName(row)}</div>
                    <div className="text-xs text-on-surface-variant">
                      {row.profil?.utilisateur?.profilApprenant?.matricule ?? row.profil?.utilisateur?.email}
                    </div>
                  </td>
                  <td className="px-4 py-3">{TYPE_LABELS[row.typeDocument] ?? row.typeDocument}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                        row.statutValidation === 'VALIDE'
                          ? 'bg-emerald-100 text-emerald-800'
                          : row.statutValidation === 'REJETE'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-amber-100 text-amber-900'
                      }`}
                    >
                      {STATUT_LABELS[row.statutValidation] ?? row.statutValidation}
                    </span>
                    {row.commentaireAdmin ? (
                      <p className="mt-1 text-xs text-on-surface-variant">{row.commentaireAdmin}</p>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-xs text-on-surface-variant">
                    {row.dateCreation ? new Date(row.dateCreation).toLocaleString('fr-FR') : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setPreview({
                            title: TYPE_LABELS[row.typeDocument] ?? row.typeDocument,
                            subtitle: personName(row),
                            apiPath: `/admin/documents/${encodeURIComponent(row.id)}/fichier`,
                          })
                        }
                        className={`${actionBtnOutline} border-primary/25 bg-primary/5 text-primary hover:bg-primary/10`}
                      >
                        <LucideSymbolIcon name="visibility" tone="inherit" size={16} />
                        Voir
                      </button>
                      {filter === 'EN_ATTENTE' && row.statutValidation === 'EN_ATTENTE' ? (
                        <>
                          <button
                            type="button"
                            onClick={() => setIdentityReview(row)}
                            className={`${actionBtnOutline} border-violet-200 bg-violet-50 text-violet-900 hover:bg-violet-100`}
                            title="Pièce d'identité et catégorie d'âge"
                          >
                            <LucideSymbolIcon name="badge" tone="inherit" size={16} />
                            Catégorie & ID
                          </button>
                          <button
                            type="button"
                            disabled={saving}
                            className={`${actionBtnOutline} border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 disabled:opacity-50`}
                            onClick={() => void handleValidate(row.id)}
                          >
                            <LucideSymbolIcon name="check_circle" tone="inherit" size={16} />
                            Valider
                          </button>
                          <button
                            type="button"
                            disabled={saving}
                            className={`${actionBtnOutline} border-red-200 bg-red-50 text-red-800 hover:bg-red-100 disabled:opacity-50`}
                            onClick={() => setRejectId(row.id)}
                          >
                            <LucideSymbolIcon name="cancel" tone="inherit" size={16} />
                            Rejeter
                          </button>
                        </>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <AdminPagination
            className="border-t border-outline-variant/15 px-4 py-3"
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            summary={summary}
          />
        </div>
      )}

      {rejectId ? (
        <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-5 shadow-sm">
          <p className="text-sm font-bold text-red-900">Motif de rejet *</p>
          <textarea
            className="mt-2 w-full rounded-lg border border-red-200 bg-white p-3 text-sm text-slate-900 focus:border-red-400 focus:outline-none focus:ring-1 focus:ring-red-300"
            rows={3}
            value={rejectMotif}
            onChange={(e) => setRejectMotif(e.target.value)}
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={saving || !rejectMotif.trim()}
              className="inline-flex items-center gap-1.5 rounded-lg bg-red-700 px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
              onClick={() => void handleReject()}
            >
              <LucideSymbolIcon name="cancel" tone="inherit" size={16} className="text-white" />
              Confirmer le rejet
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg border border-outline-variant/30 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:bg-surface-container-low"
              onClick={() => {
                setRejectId(null);
                setRejectMotif('');
              }}
            >
              Annuler
            </button>
          </div>
        </div>
      ) : null}

      <DocumentPreviewModal
        open={preview !== null}
        apiPath={preview?.apiPath ?? ''}
        title={preview?.title ?? 'Document'}
        subtitle={preview?.subtitle}
        onClose={() => setPreview(null)}
      />

      <LearnerIdentityReviewModal
        open={identityReview !== null}
        data={identityReview ? identityReviewFromRow(identityReview) : null}
        onClose={() => setIdentityReview(null)}
      />
    </AdminPageShell>
  );
}
